var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "../types.js";
import { registerSystem, registerUIComponent, DEPTHS } from "../ComponentRegistry.js";
import { EntityTypeSchema } from "../entity/Types.js";
import RAPIER from "@dimforge/rapier2d-compat";
import { flags } from "../../console/flags.js";
import * as PIXI from "pixi.js";
import { F as FrameRateSchema } from "../../FrameRate-7ce72a03.js";
import { C as CollisionsSchema, a as CollisionFiltersSchema } from "../../Collisions-9e9492fb.js";
import { P as PhysicsSchema } from "../../Physics-8d447f7a.js";
import { C as ComponentCategory } from "../../enums-86f58524.js";
import "../../decorators/type.js";
import "ajv";
import "../../utils/clone.js";
import "bitecs";
import "../../utils/typehelpers.js";
import "../../global-33d5eebf.js";
import "../../utils/byteArray.js";
import "../../persist/persist.js";
import "../../console/editor.js";
import "../../console/floatingwindow.js";
import "lodash";
import "../../Description-c36d0b9a.js";
import "../../Attach-abbbd93e.js";
import "../../utils/vector.js";
import "../../utils/windowSessionStorage.js";
class PhysicsSystem {
  constructor() {
    __publicField(this, "type", "Physics");
    __publicField(this, "category", ComponentCategory.CORE);
    __publicField(this, "schema", PhysicsSchema);
    __publicField(this, "depth", DEPTHS.COLLISION);
    __publicField(this, "world");
    __publicField(this, "eventQueue");
    __publicField(this, "colliderHandleMap", {
      handleToEntity: {},
      entityToHandle: {}
    });
    __publicField(this, "coreEntity");
  }
  init(entity, gameModel) {
    this.coreEntity = entity;
  }
  getEngine(gameModel) {
    if (!this.world) {
      const physics = gameModel.getTypedUnsafe(this.coreEntity, PhysicsSchema);
      this.world = new RAPIER.World({ x: physics.gravityX, y: physics.gravityY });
      this.world.timestep = 0.016;
      this.eventQueue = new RAPIER.EventQueue(false);
    }
    return this.world;
  }
  runAll(gameModel) {
    const dt = gameModel.dt(this.coreEntity);
    const simulatedFrames = Math.round(dt / 16.666666666666668);
    const collisionsSchema = gameModel.getTypedUnsafe(this.coreEntity, CollisionsSchema);
    collisionsSchema.collisions = {};
    const collisionMap = collisionsSchema.collisionMap;
    if (gameModel.hasComponent(this.coreEntity, FrameRateSchema)) {
      const frameRateSchema = gameModel.getTypedUnsafe(this.coreEntity, FrameRateSchema);
      frameRateSchema.bodies = this.world.bodies.len();
    }
    for (let i = 0; i < simulatedFrames; i++) {
      this.world.step(this.eventQueue);
      this.eventQueue.drainCollisionEvents((handle1, handle2, started) => {
        const eid1 = this.colliderHandleMap.handleToEntity[handle1];
        const eid2 = this.colliderHandleMap.handleToEntity[handle2];
        if (eid1 === void 0 || eid2 === void 0) {
          return;
        }
        if (started) {
          collisionMap[eid1] = collisionMap[eid1] || {};
          collisionMap[eid2] = collisionMap[eid2] || {};
          collisionMap[eid1][eid2] = true;
          collisionMap[eid2][eid1] = true;
        } else {
          if (collisionMap[eid1])
            delete collisionMap[eid1][eid2];
          if (collisionMap[eid2])
            delete collisionMap[eid2][eid1];
        }
      });
      const pairs = Object.keys(collisionMap).reduce((acc, eid1) => {
        const eid2s = Object.keys(collisionMap[parseInt(eid1)]);
        for (const eid2 of eid2s) {
          if (eid1 > eid2)
            continue;
          acc.add(parseInt(eid1) << 16 | parseInt(eid2));
        }
        return acc;
      }, /* @__PURE__ */ new Set());
      for (const pair of pairs) {
        const eid1 = pair >> 16;
        const eid2 = pair & 65535;
        if (isNaN(eid1) || isNaN(eid2))
          continue;
        if (!collisionsSchema.collisions[eid1])
          collisionsSchema.collisions[eid1] = {};
        if (!collisionsSchema.collisions[eid2])
          collisionsSchema.collisions[eid2] = {};
        collisionsSchema.collisions[eid1][eid2] = true;
        collisionsSchema.collisions[eid2][eid1] = true;
        if (gameModel.hasComponent(eid1, CollisionFiltersSchema)) {
          const filtersSchema = gameModel.getTypedUnsafe(eid1, CollisionFiltersSchema);
          if (filtersSchema.filters.length > 0) {
            EntityTypeSchema.id = eid2;
            const entityType = EntityTypeSchema.entityType;
            if (!collisionsSchema.collisions[eid1].filters)
              collisionsSchema.collisions[eid1].filters = {};
            if (filtersSchema.filters.includes(entityType)) {
              if (!collisionsSchema.collisions[eid1].filters[entityType])
                collisionsSchema.collisions[eid1].filters[entityType] = [];
              collisionsSchema.collisions[eid1].filters[entityType].push(eid2);
            }
          }
        }
        if (gameModel.hasComponent(eid2, CollisionFiltersSchema)) {
          const filtersSchema = gameModel.getTypedUnsafe(eid2, CollisionFiltersSchema);
          if (filtersSchema.filters.length > 0) {
            EntityTypeSchema.id = eid1;
            const entityType = EntityTypeSchema.entityType;
            if (!collisionsSchema.collisions[eid2].filters)
              collisionsSchema.collisions[eid2].filters = {};
            if (filtersSchema.filters.includes(entityType)) {
              if (!collisionsSchema.collisions[eid2].filters[entityType])
                collisionsSchema.collisions[eid2].filters[entityType] = [];
              collisionsSchema.collisions[eid2].filters[entityType].push(eid1);
            }
          }
        }
      }
    }
  }
  cleanup() {
    this.world.forEachRigidBody((body) => {
      this.world.removeRigidBody(body);
    });
    this.world.forEachCollider((collider) => {
      this.world.removeCollider(collider, false);
    });
    this.world = null;
  }
}
registerSystem(PhysicsSystem);
let lines;
registerUIComponent(
  "Physics",
  (uiService, entity, gameModel, viewport) => {
    if (flags.PHYSICS) {
      if (!lines) {
        lines = new PIXI.Graphics();
        lines.zIndex = Number.MAX_SAFE_INTEGER;
        viewport.addChild(lines);
      }
      const buffers = gameModel.getSystem(PhysicsSystem).world.debugRender();
      const vtx = buffers.vertices;
      const cls = buffers.colors;
      lines.clear();
      for (let i = 0; i < vtx.length / 4; i += 1) {
        const color = PIXI.Color.shared.setValue([cls[i * 8], cls[i * 8 + 1], cls[i * 8 + 2]]).toHex();
        lines.lineStyle(1, color, cls[i * 8 + 3], 0.5, true);
        lines.moveTo(vtx[i * 4], vtx[i * 4 + 1]);
        lines.lineTo(vtx[i * 4 + 2], vtx[i * 4 + 3]);
      }
    } else if (lines) {
      lines.clear();
      lines.destroy();
      lines = void 0;
    }
  },
  {
    cleanup: () => {
      if (lines) {
        lines.clear();
        lines.destroy();
        lines = void 0;
      }
    }
  }
);
export {
  PhysicsSystem
};
//# sourceMappingURL=Physics.js.map
